/* -------------------------------------------------------------------------- */
/* --------------------------- THEME : FRAMEWORKS --------------------------- */

// FRAMEWORKS : NODES_MODULES
import gsap from "gsap";
import "gsap/EasePack";
import ScrollTrigger from "gsap/ScrollTrigger";
import SplitText from "gsap/SplitText";
import ScrollSmoother from "gsap/ScrollSmoother";
gsap.registerPlugin(ScrollSmoother, ScrollTrigger, SplitText);

import Swiper from "swiper";
import {
	Autoplay,
	FreeMode,
	Thumbs,
	EffectFade,
	Navigation,
	Pagination,
	Scrollbar,
} from "swiper/modules";

/* -------------------------------------------------------------------------- */
/* ------------------------ THEME : GLOBAL VARIABLES ------------------------ */

let scrollSmooth = false;
const hzBreakpointWidth = 1440;
const hzBreakpointHeight = 680;

/* -------------------------------------------------------------------------- */
/* ------------------------- THEME : GLOBAL EVENTS -------------------------- */

// DOCUMENT READY :
document.addEventListener("DOMContentLoaded", () => {
	// remove 'no-js' body class
	document.body.classList.remove("no-js");
	gsapPreloader();
	scrollDetection(50);
	menuBurger();
	menuProduct();
	menuDropdown();
	initBanner();
	bannerToMarquee();
	popinInit();
	toggleInit();
	accordionInit();
	srcInit();
	brandsFilterInit();
	// ScrollTrigger.refresh();
});

// Rafraîchir les ScrollTriggers si le DOM change après le chargement
ScrollTrigger.addEventListener("refreshInit", () => {
	gsapScrollSmootherRefresh();
	bannerToMarquee();
	gsapFollow();
});

/* -------------------------------------------------------------------------- */
/* ----------------------- THEME : GLOBAL FUNCTIONS ------------------------- */

// Activation/désactivation du scroll
function scrollEnabled(bool) {
	if (bool === false || bool === true) {
		const enableScroll = bool === true;

		if (scrollSmooth) {
			scrollSmooth.paused(!enableScroll);
		}

		document.documentElement.style.overflowY = enableScroll ? "auto" : "hidden";
	}
}

// Fonction pour vérifier si l'appareil est tactile
function isTouchDevice() {
	return (
		"ontouchstart" in window || // Vérifie si l'événement 'touchstart' est supporté
		navigator.maxTouchPoints > 0 || // Vérifie si l'appareil a des points de contact
		navigator.msMaxTouchPoints > 0 // Pour la compatibilité avec certains navigateurs MS
	);
}

// Scroll vers une ancre
const scrollToAnchor = (scrollOffset = 100) => {
	// Fonction pour gérer le défilement
	const scrollToElement = (target, smooth = true) => {
		const element = document.querySelector(target);
		if (element) {
			if (scrollSmooth) {
				// Avec ScrollSmoother
				scrollSmooth.scrollTo(target, smooth, `top ${scrollOffset}px`);
			} else {
				// Par défaut
				window.scrollTo({
					top:
						element.getBoundingClientRect().top + window.scrollY - scrollOffset,
					behavior: smooth ? "smooth" : "auto",
				});
			}
		}
	};

	// au chargement
	const hashInUrl = window.location.hash;
	if (hashInUrl) {
		scrollToElement(hashInUrl);
	}

	// avec un [href="#..."]
	const linksWithAnchor = document.querySelectorAll(
		'a[href^="#"]:not([disabled])',
	);
	linksWithAnchor.forEach((link) => {
		link.addEventListener("click", (event) => {
			const anchorHref = link.getAttribute("href");
			if (anchorHref !== "#") {
				event.preventDefault();
				scrollToElement(anchorHref);
			}
		});
	});

	// avec un [data-scrollto]
	const dataWithScrollTo = document.querySelectorAll(
		"[data-scrollto]:not([disabled])",
	);
	dataWithScrollTo.forEach((data) => {
		data.addEventListener("click", () => {
			let anchorData = data.getAttribute("data-scrollto");
			if (anchorData === "auto") {
				// Calculer la position de défilement pour 'auto'
				const autoOffset = window.innerHeight + window.scrollY - scrollOffset;
				window.scrollTo({
					top: autoOffset,
					behavior: "smooth",
				});
			} else {
				// Utiliser la valeur de data-scrollto comme cible pour scrollToElement
				scrollToElement(anchorData);
			}
		});
	});
};

// Détection du scroll sur le body
const scrollDetection = (scrollOffset = 1) => {
	let position = window.scrollY;

	if (position > scrollOffset) {
		document.body.classList.add("page-with-scrolled");
	} else {
		document.body.classList.remove("page-with-scrolled");
	}

	document.addEventListener("scroll", () => {
		// Détecter la direction du scroll
		let scroll = window.scrollY;
		// Scroll vers le bas
		if (scroll > position) {
			if (scroll < scrollOffset) {
				document.body.classList.remove("page-with-scrolled");
				document.body.classList.remove("page-with-scrolling");
			} else {
				document.body.classList.add("page-with-scrolling");
				document.body.classList.add("page-with-scrolled");
			}
		}
		// Scroll vers le haut
		else {
			document.body.classList.remove("page-with-scrolling");
			if (scroll < scrollOffset) {
				document.body.classList.remove("page-with-scrolled");
			}
		}
		// Scroll max ?
		if (scroll + window.innerHeight >= document.documentElement.scrollHeight) {
			document.body.classList.add("page-scroll-completed");
		} else {
			document.body.classList.remove("page-scroll-completed");
		}
		position = scroll;
	});
};

// Gestion du menu burger
const menuBurger = () => {
	const headerBurgerButton = document.querySelector("#page-header-burger");
	const headerBurgerArea = document.querySelector("#page-header-fullmenu");
	const headerProductArea = document.querySelector("#page-header-popin");

	if (headerBurgerButton) {
		headerBurgerButton.addEventListener("click", () => {
			if (document.body.classList.contains("page-with-fullmenu")) {
				scrollEnabled(true);
				document.body.classList.remove("page-with-fullmenu");
			} else {
				scrollEnabled(false);
				document.body.classList.add("page-with-fullmenu");
			}
		});

		document.addEventListener("mouseup", (event) => {
			if (
				document.body.classList.contains("page-with-fullmenu") &&
				!headerBurgerButton.contains(event.target) &&
				headerBurgerButton !== event.target &&
				!headerBurgerArea.contains(event.target) &&
				headerBurgerArea !== event.target &&
				!headerProductArea.contains(event.target) &&
				headerProductArea !== event.target
			) {
				scrollEnabled(true);
				document.body.classList.remove("page-with-fullmenu");
			}
		});
	}
};

// Gestion du menu de produits
const menuProduct = () => {
	const headerProductButtons = document.querySelectorAll(
		'[data-menu="product"]',
	);
	const headerProductArea = document.querySelector("#page-header-popin");

	if (!headerProductButtons.length || !headerProductArea) return;

	const togglePopin = () => {
		if (document.body.classList.contains("page-with-popin")) {
			scrollEnabled(true);
			document.body.classList.remove("page-with-popin");
		} else {
			scrollEnabled(false);
			document.body.classList.add("page-with-popin");
		}
	};

	headerProductButtons.forEach((button) => {
		button.addEventListener("click", () => {
			togglePopin();
		});
	});

	document.addEventListener("mouseup", (event) => {
		if (!document.body.classList.contains("page-with-popin")) return;

		const clickedOnButton = Array.from(headerProductButtons).some(
			(btn) => btn.contains(event.target) || btn === event.target,
		);

		const clickedInsidePopin =
			headerProductArea.contains(event.target) ||
			headerProductArea === event.target;

		if (!clickedOnButton && !clickedInsidePopin) {
			scrollEnabled(true);
			document.body.classList.remove("page-with-popin");
		}
	});
};

// Gestion des dropdowns
const menuDropdown = () => {
	const menuItemLinks = document.querySelectorAll(
		'.menu .menu__item .menu__link[href="#"]',
	);

	menuItemLinks.forEach((item) => {
		item.closest(".menu__item").classList.add("menu__item--parent");
		item.addEventListener("click", (event) => {
			event.preventDefault();
			item.closest(".menu__item").classList.toggle("menu__item--active");
		});
	});

	document.addEventListener("mouseup", (event) => {
		const activeMenuItems = document.querySelectorAll(
			".menu .menu__item.menu__item--active",
		);

		activeMenuItems.forEach((activeItem) => {
			if (
				!activeItem.isEqualNode(event.target) &&
				!activeItem.contains(event.target)
			) {
				activeItem.classList.remove("menu__item--active");
			}
		});
	});
};

// Activation/désactivation de la bannière
const initBanner = () => {
	// Vérifie si la bannière a déjà été fermée durant cette session
	if (sessionStorage.getItem("bannerClosed") === "true") {
		return;
	}

	const pageBanner = document.querySelector("#page-banner");
	const pageBannerClose = document.querySelector("#page-banner-close");

	if (pageBanner) {
		// actualisation
		document.body.classList.add("page-with-banner");
		// bouton de fermeture
		pageBannerClose.addEventListener("click", () => {
			document.body.classList.remove("page-with-banner");

			// Enregistre en session que la bannière a été fermée
			sessionStorage.setItem("bannerClosed", "true");
		});
	}
};

// Gestion du défilement horizontal de la bannière
const bannerToMarquee = () => {
	const pageBanner = document.querySelector("#page-banner");

	if (pageBanner) {
		const bannerwrapperWidth = pageBanner.offsetWidth;
		const bannerTextWidth =
			pageBanner.querySelector(".banner__text").offsetWidth;
		if (bannerwrapperWidth > bannerTextWidth + 60) {
			pageBanner.classList.remove("--scrolling");
		} else {
			pageBanner.classList.add("--scrolling");
		}
	}
};

// Gestions des actions des [data-toggle]
const toggleInit = () => {
	// Récupération de tous les écouteurs
	const toggleListeners = document.querySelectorAll("[data-toggle]");

	// Écouteur d'événement pour chaque écouteur
	toggleListeners.forEach((toggleListener) => {
		const toggleTargetSelector = toggleListener.dataset.toggle;

		// Ajout d'un événement "click" à chaque écouteur
		toggleListener.addEventListener("click", (event) => {
			// event.stopPropagation();

			// Désactiver tous les toggles et leurs cibles
			toggleListeners.forEach((listener) => {
				const targets = listener.dataset.toggle
					? document.querySelectorAll(listener.dataset.toggle)
					: null;

				if (targets) {
					listener.classList.remove("--active");
					targets.forEach((target) => target.classList.remove("--active"));
				}
			});

			// Récupérer les toggles ayant le même sélecteur de cible
			const similarToggleListeners = document.querySelectorAll(
				`[data-toggle="${toggleTargetSelector}"]`,
			);
			const toggleTargets = toggleTargetSelector
				? document.querySelectorAll(toggleTargetSelector)
				: null;

			// Vérifier si les éléments sont actifs
			const isActive =
				toggleTargets &&
				Array.from(toggleTargets).every((target) =>
					target.classList.contains("--active"),
				);

			// Si le toggle cliqué n'était pas actif, l'activer avec ses cibles
			if (!isActive && toggleTargets) {
				similarToggleListeners.forEach((listener) => {
					listener.classList.add("--active");
				});

				// Activer toutes les cibles associées
				toggleTargets.forEach((target) => target.classList.add("--active"));
			}
		});
	});
};

// Gestions des actions des [data-accordion]
const accordionInit = () => {
	const accordionListeners = document.querySelectorAll("[data-accordion]");

	accordionListeners.forEach((accordionElement) => {
		accordionElement.addEventListener("click", (event) => {
			// event.stopPropagation();

			// Récupérer l'élément cible de l'accordéon actuel
			const currentAccordionTarget = document.querySelector(
				`#${accordionElement.dataset.accordion}`,
			);

			// Vérifier si l'accordéon cliqué est déjà actif
			const isActive = accordionElement.classList.contains("--active");

			// On change l'état de l'accordéon
			if (!isActive && currentAccordionTarget) {
				accordionElement.classList.add("--active");
				currentAccordionTarget.classList.add("--active");
			} else {
				accordionElement.classList.remove("--active");
				currentAccordionTarget.classList.remove("--active");
			}
		});
	});
};

// Gestions des actions des [data-popin]
const popinInit = () => {
	const popins = document.querySelectorAll("[data-popin]");

	popins.forEach((popin) => {
		popin.addEventListener("click", (event) => {
			event.preventDefault();

			// ouvrir/fermer la popin
			const thispopinName = popin.dataset.popin;
			if (thispopinName) {
				const thispopinEl = document.querySelector(`#${thispopinName}`);
				thispopinEl.classList.toggle("--open");

				// Vérifier si la classe --open est présente pour bloquer ou débloquer le scroll
				if (thispopinEl.classList.contains("--open")) {
					scrollEnabled(false);
				} else {
					scrollEnabled(true);
				}
			}
		});
	});
};

// Conversion des [data-src] en [src]
const srcInit = () => {
	// Select all elements with a data-src attribute
	const srcElements = document.querySelectorAll("[data-src]");
	srcElements.forEach((element) => {
		element.setAttribute("src", element.getAttribute("data-src"));
		element.removeAttribute("data-src");
	});
};

// Gestion des filtres de la page #marques
const brandsFilterInit = () => {
	const pageMarque = document.querySelector("#marques");
	if (!pageMarque) return;

	const letters = document.querySelectorAll("[data-letter]");
	const letterTargets = document.querySelectorAll("[data-letter-target]");
	const categories = document.querySelectorAll("[data-category]");
	const categoryTargets = document.querySelectorAll("[data-category-target]");
	const reset = document.querySelector("[data-reset]");

	// Refraichis les filtres
	function resetFilters(exception) {
		switch (exception) {
			case "categories":
				// Gérer les data-category
				categories.forEach((category) => {
					category.classList.remove("--active");
				});
				break;
			case "letters":
				letters.forEach((letter) => {
					letter.classList.remove("--active");
				});
				break;
			default:
				// Gérer les data-category
				categories.forEach((category) => {
					category.classList.remove("--active");
				});
				// Gérer les data-letter
				letters.forEach((letter) => {
					letter.classList.remove("--active");
				});
				// Gérer les letterTargets individuels (slides)
				letterTargets.forEach((target) => {
					target.classList.remove("--hidden");
				});
				// MAJ sur les [data-category-target]
				checkCategories(categoryTargets);
				// Gérer le button reset
				reset.classList.add("--hidden");
		}
	}

	// Refraichis les [data-letter-target]
	function checkCategories(categoryTargets) {
		categoryTargets.forEach((categoryTarget) => {
			const letterTargets = categoryTarget.querySelectorAll(
				"[data-letter-target]",
			);
			// Vérifie si tous les éléments [data-letter-target] de categoryTarget contiennent la classe "--hidden"
			const allHidden = Array.from(letterTargets).every((target) =>
				target.classList.contains("--hidden"),
			);
			if (allHidden) {
				categoryTarget.classList.add("--hidden");
			} else {
				categoryTarget.classList.remove("--hidden");
			}
		});
	}

	// Ajoute la classe "button-disabled" aux lettres sans cible correspondante
	const availableLetter = new Set(
		// Crée un Set pour les lettres qui ont des cibles correspondantes
		[...letterTargets].map((target) => target.dataset.letterTarget),
	);
	letters.forEach((letter) => {
		if (!availableLetter.has(letter.dataset.letter)) {
			letter.classList.add("button-disabled");
			letter.setAttribute("disabled", true);
		}
	});

	// Actions pour chaque [data-category]
	categories.forEach((category) => {
		// Gestion du clic sur les éléments data-category
		category.addEventListener("click", () => {
			const clickedcategory = category.dataset.category;

			// Si le bouton a la classe "button-disabled" ou l'attribut "disabled" ou aucune valeur pour data.category, ne rien faire
			if (
				category.classList.contains("button-disabled") ||
				category.hasAttribute("disabled") ||
				clickedcategory == ""
			) {
				return;
			}

			// Gérer les [data-category-target]
			categoryTargets.forEach((target) => {
				const isMatch = target.dataset.categoryTarget === clickedcategory;
				if (isMatch) {
					target
						.querySelectorAll("[data-letter-target]")
						.forEach((letterTarget) => {
							letterTarget.classList.remove("--hidden");
						});
				} else {
					target
						.querySelectorAll("[data-letter-target]")
						.forEach((letterTarget) => {
							letterTarget.classList.add("--hidden");
						});
				}
			});

			// Gérer les [data-category]
			categories.forEach((l) => l.classList.remove("--active"));
			category.classList.add("--active");

			// MAJ sur les [data-category-target]
			checkCategories(categoryTargets);

			// Activer le bouton reset
			reset.classList.remove("--hidden");

			// Réinitialisér les lettres disponibles
			resetFilters("letters");
		});
	});

	// Actions pour chaque [data-letter]
	letters.forEach((letter) => {
		// Gestion du clic sur les éléments data-letter
		letter.addEventListener("click", () => {
			const clickedLetter = letter.dataset.letter;

			// Si le bouton a la classe "button-disabled" ou l'attribut "disabled" ou aucune valeur pour data.letter, ne rien faire
			if (
				letter.classList.contains("button-disabled") ||
				letter.hasAttribute("disabled") ||
				clickedLetter == ""
			) {
				return;
			}

			// Gérer les letterTargets individuels (slides)
			letterTargets.forEach((target) => {
				const isMatch = target.dataset.letterTarget === clickedLetter;
				if (isMatch) {
					target.classList.remove("--hidden");
				} else {
					target.classList.add("--hidden");
				}
			});

			// Gérer les data-letter
			letters.forEach((l) => l.classList.remove("--active"));
			letter.classList.add("--active");

			// MAJ sur les [data-category-target]
			checkCategories(categoryTargets);

			// Activer le bouton reset
			reset.classList.remove("--hidden");

			// Réinitialisér les lettres disponibles
			resetFilters("categories");
		});
	});

	// Gestion du clic sur le bouton letterResets
	reset.addEventListener("click", resetFilters);
};

/* -------------------------------- (SWIPER) -------------------------------- */

// Swiper : Initialisation des sliders
const swiperInit = () => {
	const swiperListeners = document.querySelectorAll(".swiper[data-swiper]");

	swiperListeners.forEach((swiper) => {
		const slides = swiper.querySelectorAll(".swiper-wrapper .swiper-slide");

		if (slides.length > 1) {
			const swiperAttribute = swiper.getAttribute("data-swiper") || "";
			const swiperOptions = swiperAttribute.trim().split(/\s+/);

			// Si l'attribut est pour un Swiper de type lié (menu-xxx, product-xxx), on l'ignore dans cette boucle.
			if (
				swiperOptions.includes("menu-thumbs") ||
				swiperOptions.includes("menu-links") ||
				swiperOptions.includes("product-thumbs") ||
				swiperOptions.includes("product-medias")
			) {
				return;
			}

			// Configuration de base
			let config = {
				// Paramètres de base
				slidesPerView: "auto",
				speed: 1200, // Vitesse par défaut pour les Swipers simples (1200ms dans votre script original)
				resizeObserver: true,
				// Modules de base à inclure si les options correspondantes sont trouvées
				modules: [Navigation, Pagination, Scrollbar],
				navigation: {
					prevEl:
						swiper.querySelector(".swiper-navigation-prev") ||
						swiper.querySelector(".swiper-button-prev"),
					nextEl:
						swiper.querySelector(".swiper-navigation-next") ||
						swiper.querySelector(".swiper-button-next"),
				},
				pagination: {
					el: swiper.querySelector(".swiper-pagination"),
					clickable: true,
				},
				scrollbar: {
					el: swiper.querySelector(".swiper-scrollbar"),
					draggable: true,
					hide: true,
				},
			};

			// [data-swiper="freeMode"]
			if (swiperOptions.includes("freemode")) {
				config.modules.push(FreeMode);
				config.freeMode = { enabled: true };
			}

			// [data-swiper="autoplay"]
			if (swiperOptions.includes("autoplay")) {
				config.modules.push(Autoplay);
				// Paramètres d'Autoplay par défaut
				config.autoplay = {
					delay: 3600, // Une valeur moyenne si non spécifié plus tard
					disableOnInteraction: true,
				};
			}

			// [data-swiper="Loop"]
			if (swiperOptions.includes("loop")) {
				config.loop = true;
			}

			// [data-swiper="brands"]
			if (swiperAttribute === "brands") {
				const slideCount = slides.length;
				config.speed = slideCount * 100;
				config.loop = true;
				config.autoplay = { delay: 0 };
				config.modules.push(FreeMode);
				config.freeMode = { enabled: true };
			}

			// [data-swiper="partners"]
			if (swiperAttribute === "partners") {
				config.modules.push(Autoplay);
				config.autoplay = {
					delay: 1600,
					disableOnInteraction: true,
				};
				config.loop = true;
				config.speed = 1800;
				config.direction = "horizontal";
				config.breakpoints = {
					[hzBreakpointWidth]: {
						direction: "vertical",
					},
				};
			}

			// [data-swiper="category"]
			if (swiperAttribute === "category") {
				config.modules.push(FreeMode);
				config.freeMode = { enabled: true };
				config.speed = 1200;
				config.breakpoints = {
					1440: { enabled: false },
				};
			}

			// Initialisation du Swiper
			// console.log(config);
			new Swiper(swiper, config);
		}
	});

	// [data-swiper='menu-thumbs'] + [data-swiper='menu-links']
	const swiperMenuThumbsEl = document.querySelector(
		".swiper[data-swiper='menu-thumbs']",
	);
	const swiperMenuLinksEl = document.querySelector(
		".swiper[data-swiper='menu-links']",
	);
	if (
		swiperMenuThumbsEl &&
		swiperMenuLinksEl &&
		swiperMenuThumbsEl.querySelectorAll(".swiper-slide").length > 1 &&
		swiperMenuLinksEl.querySelectorAll(".swiper-slide").length > 1
	) {
		const swiperMenuThumbs = new Swiper(swiperMenuThumbsEl, {
			slidesPerView: "auto",
			speed: 600,
			resizeObserver: true,
			watchSlidesProgress: true,
			modules: [FreeMode, Navigation],
			freeMode: { enabled: true },
			navigation: {
				nextEl: swiperMenuThumbsEl.querySelector(".swiper-button-next"),
				prevEl: swiperMenuThumbsEl.querySelector(".swiper-button-prev"),
			},
		});
		new Swiper(swiperMenuLinksEl, {
			slidesPerView: "auto",
			speed: 300,
			resizeObserver: true,
			modules: [EffectFade, Thumbs],
			effect: "fade",
			thumbs: { swiper: swiperMenuThumbs },
		});
	}

	// [data-swiper='product-thumbs'] + [data-swiper='product-medias']
	const swiperProductThumbsEl = document.querySelector(
		".swiper[data-swiper='product-thumbs']",
	);
	const swiperProductMediasEl = document.querySelector(
		".swiper[data-swiper='product-medias']",
	);
	if (
		swiperProductThumbsEl &&
		swiperProductMediasEl &&
		swiperProductThumbsEl.querySelectorAll(".swiper-slide").length > 1 &&
		swiperProductMediasEl.querySelectorAll(".swiper-slide").length > 1
	) {
		const swiperProductThumbs = new Swiper(swiperProductThumbsEl, {
			slidesPerView: "auto",
			speed: 600,
			resizeObserver: true,
			watchSlidesProgress: true,
			modules: [FreeMode],
			freeMode: { enabled: true },
		});
		new Swiper(swiperProductMediasEl, {
			slidesPerView: "auto",
			speed: 600,
			resizeObserver: true,
			modules: [Thumbs],
			thumbs: { swiper: swiperProductThumbs },
		});
	}
};

/* --------------------------------- (GSAP) --------------------------------- */

// GSAP : Initialisation du scrollSmoother (avec ScrollSmoother)
const initScrollSmooth = () => {
	if (!isTouchDevice()) {
		scrollSmooth = ScrollSmoother.create({
			wrapper: "#page-wrapper",
			content: "#page-container",
			smooth: 1,
			smoothTouch: false,
			effects: true,
			normalizeScroll: false,
			ignoreMobileResize: true,
		});
	}
};

// GSAP : Initialisation du scroll horizontal
const initHorizontalScroll = (
	bpWidth = hzBreakpointWidth,
	bpHeight = hzBreakpointHeight,
) => {
	if (document.body.classList.contains("template-home")) {
		let mm = gsap.matchMedia();

		// Format vertical classique (mobile)
		mm.add(
			`(max-width: ${bpWidth - 0.02}px) or (max-height: ${bpHeight - 0.02}px)`,
			() => {
				// Scroll classique
				document.body.classList.remove("page-with-horizontal-scroll");
				gsapAnimations();
			},
		);

		// Format horizontal (desktop)
		mm.add(`(min-width: ${bpWidth}px) and (min-height: ${bpHeight}px)`, () => {
			// Scroll horizontal
			document.body.classList.add("page-with-horizontal-scroll");
			let horizontalWrapper = document.querySelector("#page-article");
			let horizontalScroll = gsap.to(horizontalWrapper, {
				x: () =>
					-1 *
						(horizontalWrapper.scrollWidth -
							document.documentElement.clientWidth) +
					"px",
				ease: "none",
			});
			ScrollTrigger.create({
				trigger: horizontalWrapper,
				start: "top top",
				end: () => "+=" + horizontalWrapper.offsetWidth,
				scrub: 1,
				pin: true,
				pinSpacing: true,
				invalidateOnRefresh: true,
				animation: horizontalScroll,
				// onUpdate: function () {
				// 	const totalHzScroll = document.body.scrollHeight - window.innerHeight;
				// 	let currentHzScroll = window.scrollY;
				// 	console.log(`${Math.round((currentHzScroll * 100) / totalHzScroll)}%`);
				// },
				// markers: true,
			});
			gsapHorizontalAnimations(horizontalScroll);
		});
	}
};

// GSAP : Découpage des textes (avec SplitText)
const gsapSplitText = () => {
	const textsToAnimated = document.querySelectorAll([
		// global
		'[data-animation^="reveal-text"]',
		".elementor-heading-title",
	]);

	textsToAnimated.forEach((_thisText) => {
		new SplitText(_thisText, {
			tag: "span",
			// type: "lines, words, chars",
			type: "lines, words",
			linesClass: "line",
			wordsClass: "word",
			charsClass: "char",
		});
	});
};

// GSAP : Animations au scroll (avec ScrollTrigger)
const gsapAnimations = () => {
	document.body.classList.add("page-with-animations");
	if (!document.body.classList.contains("page-with-horizontal-scroll")) {
		// [data-animation] + .elementor-element
		const elementsToAnimated = document.querySelectorAll([
			"[data-animation]",
			".elementor-heading-title",
			".elementor-element > .elementor-element > .elementor-element:not([data-animation])",
		]);
		elementsToAnimated.forEach((_this) => {
			const delay = _this.getAttribute("data-animation-delay")
				? parseInt(_this.getAttribute("data-animation-delay"), 10)
				: 0;
			ScrollTrigger.create({
				trigger: _this,
				start: "top 100%",
				end: "bottom 0",
				onEnter: () => {
					setTimeout(() => {
						_this.classList.add("--animated");
					}, delay);
				},
				onLeaveBack: () => {
					_this.classList.remove("--animated");
				},
				// markers: true,
			});
		});

		// [data-animation-count]
		const elementsToCounter = document.querySelectorAll(
			"[data-animation-count]",
		);
		elementsToCounter.forEach((element) => {
			const delay = element.getAttribute("data-animation-delay")
				? parseInt(element.getAttribute("data-animation-delay"), 10)
				: 0;
			const raw = element.getAttribute("data-animation-count").trim();

			// (Décimale : virgule ou point suivi de 1–2 chiffres en fin de valeur)
			const commaDecMatch = raw.match(/,(\d{1,2})$/);
			const isCommaDecimal = !!commaDecMatch && !raw.match(/,\d{3}/);
			const dotDecMatch = !isCommaDecimal && raw.match(/\.(\d{1,2})$/);
			const isDotDecimal = !!dotDecMatch && !raw.match(/\.\d{3}/);
			const decimalPlaces = isCommaDecimal
				? commaDecMatch[1].length
				: isDotDecimal
					? dotDecMatch[1].length
					: 0;

			// (Séparateur de milliers : espace (insécable ou normal), virgule, ou point)
			const hasSpaceSep = /\d[  ]\d/.test(raw);
			const spaceSepChar = raw.includes(" ") ? " " : " ";
			const hasCommaSep = !isCommaDecimal && /,/.test(raw);
			const hasDotSep = !isDotDecimal && /\./.test(raw);

			// (Valeur numérique cible : nettoyage des séparateurs)
			let normalized = raw.replace(/[  ]/g, "");
			if (hasCommaSep) normalized = normalized.replace(/,/g, "");
			if (hasDotSep) normalized = normalized.replace(/\./g, "");
			if (isCommaDecimal) normalized = normalized.replace(",", ".");
			const target = parseFloat(normalized) || 0;

			// (Reconstruit la chaîne avec les séparateurs d'origine)
			const format = (val) => {
				const rounded = parseFloat(val.toFixed(decimalPlaces));
				const [intRaw, decRaw = ""] = rounded
					.toFixed(decimalPlaces)
					.split(".");
				const sep = hasSpaceSep
					? spaceSepChar
					: hasCommaSep
						? ","
						: hasDotSep
							? "."
							: "";
				const intFmt = sep
					? intRaw.replace(/\B(?=(\d{3})+(?!\d))/g, sep)
					: intRaw;
				return decimalPlaces > 0
					? intFmt + (isCommaDecimal ? "," : ".") + decRaw
					: intFmt;
			};

			element.textContent = format(0);

			const counter = { value: 0 };
			let tween = null;

			ScrollTrigger.create({
				trigger: element,
				start: "top 100%",
				onEnter: () => {
					if (tween) tween.kill();
					counter.value = 0;
					tween = gsap.timeline({ delay });
					tween.fromTo(
						element,
						{ filter: "blur(8px)" },
						{ filter: "blur(0px)", duration: 1.5, ease: "power3.out" },
						0,
					);
					tween.to(
						counter,
						{
							value: target,
							duration: 1.5,
							ease: "power3.out",
							onUpdate: () => {
								element.textContent = format(counter.value);
							},
							onComplete: () => {
								element.textContent = raw;
							},
						},
						0,
					);
				},
				onLeaveBack: () => {
					if (tween) tween.kill();
					counter.value = 0;
					element.textContent = format(0);
					gsap.set(element, { clearProps: "filter" });
				},
			});
		});

		// data-animation="reveal-text-reading"
		const textsToReading = document.querySelectorAll([
			'[data-animation="reveal-text-reading"]',
		]);
		textsToReading.forEach((_this) => {
			const delay = _this.getAttribute("data-animation-delay")
				? parseInt(_this.getAttribute("data-animation-delay"), 10)
				: 0;
			gsap.fromTo(
				_this.querySelectorAll(".word"),
				{
					opacity: 0.33,
				},
				{
					delay: delay,
					stagger: 0.3,
					ease: "power1.out",
					opacity: 1,
					scrollTrigger: {
						trigger: _this,
						start: "top 90%",
						end: "top 20%",
						scrub: true,
						// markers: true,
					},
				},
			);
		});

		// #cta-home
		const ctaHome = document.querySelector("#cta-home");
		if (ctaHome) {
			ScrollTrigger.create({
				trigger: document.querySelector(".archive-featured-product-category"),
				start: "left +=50%",
				end: "right +=0",
				onEnter: () => {
					ctaHome.classList.add("--open");
				},
				// markers: true,
			});
		}
	}
};

/* -------------------------------------------------------------------------- */

// GSAP : [data-follow]
const gsapFollow = () => {
	const maxScreenWidth = 1024;
	const dataAttribute = "data-follow";
	const followElements = document.querySelectorAll(`[${dataAttribute}]`);

	const isScreenSmall = window.innerWidth <= maxScreenWidth;

	if (followElements.length === 0) {
		return;
	}

	followElements.forEach((followElement, index) => {
		const triggerElementSelector = followElement.getAttribute(dataAttribute);
		const triggerElement = document.querySelector(triggerElementSelector);
		const followId = `followInstance-${index}`;

		if (!triggerElement) {
			return;
		}

		// Nettoyage de l'ancienne instance si elle existe
		const instance = ScrollTrigger.getById(followId);
		if (instance) {
			instance.kill();
			gsap.set(followElement, { clearProps: "all" });
		}

		// Application de l'effet si la condition de taille d'écran est remplie
		if (isScreenSmall) {
			const followHeight = 0; //followElement.offsetHeight;

			// Décalages basés sur la hauteur de l'élément à suivre
			const startValue = `top +=${followHeight}`;
			const endValue = `bottom-=${followHeight}`;

			gsap.to(followElement, {
				scrollTrigger: {
					id: followId,
					trigger: triggerElement,
					pin: followElement,
					start: startValue,
					end: endValue,
					pinSpacing: false,
					// markers: true,
				},
			});
		}
	});
};

// GSAP : Animations au scroll horizontal (avec ScrollTrigger)
const gsapHorizontalAnimations = (horizontalScroll) => {
	// [data-animation] + .elementor-element
	const elementsToAnimated = document.querySelectorAll([
		"[data-animation]",
		".elementor-heading-title",
		".elementor-element > .elementor-element > .elementor-element:not([data-animation])",
	]);
	elementsToAnimated.forEach((_this) => {
		const delay = _this.getAttribute("data-animation-delay")
			? parseInt(_this.getAttribute("data-animation-delay"), 10)
			: 0;
		ScrollTrigger.create({
			trigger: _this,
			containerAnimation: horizontalScroll,
			start: "left +=90%",
			end: "right +=0",
			onEnter: () => {
				setTimeout(() => {
					_this.classList.add("--animated");
				}, delay);
			},
			onLeaveBack: () => {
				_this.classList.remove("--animated");
			},
			// markers: true,
		});
	});

	// [data-animation-count]
	const elementsToCounter = document.querySelectorAll(
		"[data-animation-count]",
	);
	elementsToCounter.forEach((element) => {
		const delay = element.getAttribute("data-animation-delay")
			? parseInt(element.getAttribute("data-animation-delay"), 10)
			: 0;
		const raw = element.getAttribute("data-animation-count").trim();

		// (Décimale : virgule ou point suivi de 1–2 chiffres en fin de valeur)
		const commaDecMatch = raw.match(/,(\d{1,2})$/);
		const isCommaDecimal = !!commaDecMatch && !raw.match(/,\d{3}/);
		const dotDecMatch = !isCommaDecimal && raw.match(/\.(\d{1,2})$/);
		const isDotDecimal = !!dotDecMatch && !raw.match(/\.\d{3}/);
		const decimalPlaces = isCommaDecimal
			? commaDecMatch[1].length
			: isDotDecimal
				? dotDecMatch[1].length
				: 0;

		// (Séparateur de milliers : espace (insécable ou normal), virgule, ou point)
		const hasSpaceSep = /\d[  ]\d/.test(raw);
		const spaceSepChar = raw.includes(" ") ? " " : " ";
		const hasCommaSep = !isCommaDecimal && /,/.test(raw);
		const hasDotSep = !isDotDecimal && /\./.test(raw);

		// (Valeur numérique cible : nettoyage des séparateurs)
		let normalized = raw.replace(/[  ]/g, "");
		if (hasCommaSep) normalized = normalized.replace(/,/g, "");
		if (hasDotSep) normalized = normalized.replace(/\./g, "");
		if (isCommaDecimal) normalized = normalized.replace(",", ".");
		const target = parseFloat(normalized) || 0;

		// (Reconstruit la chaîne avec les séparateurs d'origine)
		const format = (val) => {
			const rounded = parseFloat(val.toFixed(decimalPlaces));
			const [intRaw, decRaw = ""] = rounded
				.toFixed(decimalPlaces)
				.split(".");
			const sep = hasSpaceSep
				? spaceSepChar
				: hasCommaSep
					? ","
					: hasDotSep
						? "."
						: "";
			const intFmt = sep
				? intRaw.replace(/\B(?=(\d{3})+(?!\d))/g, sep)
				: intRaw;
			return decimalPlaces > 0
				? intFmt + (isCommaDecimal ? "," : ".") + decRaw
				: intFmt;
		};

		element.textContent = format(0);

		const counter = { value: 0 };
		let tween = null;

		ScrollTrigger.create({
			trigger: element,
			containerAnimation: horizontalScroll,
			start: "left +=90%",
			end: "right +=0",
			onEnter: () => {
				if (tween) tween.kill();
				counter.value = 0;
				tween = gsap.timeline({ delay });
				tween.fromTo(
					element,
					{ filter: "blur(8px)" },
					{ filter: "blur(0px)", duration: 1.5, ease: "power3.out" },
					0,
				);
				tween.to(
					counter,
					{
						value: target,
						duration: 1.5,
						ease: "power3.out",
						onUpdate: () => {
							element.textContent = format(counter.value);
						},
						onComplete: () => {
							element.textContent = raw;
						},
					},
					0,
				);
			},
			onLeaveBack: () => {
				if (tween) tween.kill();
				counter.value = 0;
				element.textContent = format(0);
				gsap.set(element, { clearProps: "filter" });
			},
		});
	});

	// data-animation="reveal-text-reading"
	const textsToReading = document.querySelectorAll([
		'[data-animation="reveal-text-reading"]',
	]);
	textsToReading.forEach((_this) => {
		const delay = _this.getAttribute("data-animation-delay")
			? parseInt(_this.getAttribute("data-animation-delay"), 10)
			: 0;
		gsap.fromTo(
			_this.querySelectorAll(".word"),
			{
				opacity: 0.33,
			},
			{
				delay: delay,
				stagger: 0.3,
				ease: "power1.out",
				opacity: 1,
				scrollTrigger: {
					trigger: _this,
					containerAnimation: horizontalScroll,
					start: "left +=90%",
					end: "right +=0",
					scrub: true,
					// markers: true,
				},
			},
		);
	});

	// #cta-home
	const ctaHome = document.querySelector("#cta-home");
	if (ctaHome) {
		ScrollTrigger.create({
			trigger: document.querySelector(".archive-featured-product-category"),
			containerAnimation: horizontalScroll,
			start: "left +=50%",
			end: "right +=0",
			onEnter: () => {
				ctaHome.classList.add("--open");
			},
			// markers: true,
		});
	}
};

// GSAP : Élement fixé au scroll (avec ScrollTrigger)
const gsapStickyElements = () => {
	const stickyElements = document.querySelectorAll("[data-sticky]");
	if (!stickyElements.length) return;

	let mm = gsap.matchMedia();

	mm.add("(min-width: 1024px)", () => {
		// Tout ce qui est créé ici sera AUTOMATIQUEMENT tué
		// quand l'écran passera sous les 1024px. Fini les bugs de résidus !

		stickyElements.forEach((el) => {
			const offset = parseInt(el.dataset.sticky, 10) || 0;
			const wrapper = el.parentElement;

			ScrollTrigger.create({
				trigger: el,
				start: `top-=${offset} top`,
				end: () => `+=${wrapper.offsetHeight - el.offsetHeight}`,
				pin: true,
				pinSpacing: false,
				invalidateOnRefresh: true,
			});
		});
	});
};

// GSAP : Détecte les sections avec des #ancre visibles (avec ScrollTrigger)
const gsapAnchorsDetection = () => {
	const anchorLinks = document.querySelectorAll('.menu__link[href^="#"]');
	const menuContainer = document.querySelector("#menu .content__menu ");

	// Flag pour bloquer le scroll auto au démarrage
	let isReady = false;

	const scrollToActiveLink = (activeLink) => {
		if (menuContainer && activeLink && isReady) {
			activeLink.scrollIntoView({
				behavior: "smooth",
				block: "nearest",
				inline: "center",
			});
		}
	};

	anchorLinks.forEach((link) => {
		const targetId = link.getAttribute("href");
		if (targetId === "#") return;

		const targetSection = document.querySelector(targetId);
		if (targetSection) {
			ScrollTrigger.create({
				trigger: targetSection,
				start: "top 50%",
				end: "bottom 50%",
				onToggle: (self) => {
					if (self.isActive) {
						link.setAttribute("aria-current", "true");
						scrollToActiveLink(link);
					} else {
						link.removeAttribute("aria-current");
					}
				},
			});
		}
	});

	// On autorise le scroll du menu seulement après le rendu initial
	ScrollTrigger.refresh();
	isReady = true;
};
// Actions exécutées après le preLoader d'accueil
const afterPreloader = () => {
	scrollEnabled(true);
	swiperInit();
	gsapAnimations();
	gsapStickyElements();
	scrollToAnchor();
};

// GSAP : Rafraîchissement du ScrollSmoother (avec ScrollSmoother)
const gsapScrollSmootherRefresh = () => {
	if (scrollSmooth) {
		// Désactiver sur Élémentor
		if (document.querySelector("body.elementor-editor-active")) {
			scrollSmooth.kill();
		} else {
			scrollSmooth.refresh();
		}
	}
};

// GSAP : Gestion du preloader d'accueil
const gsapPreloader = () => {
	// Chargement des fonctions GSAP préalables
	initScrollSmooth();
	initHorizontalScroll();
	scrollEnabled(false);
	gsapAnchorsDetection();
	gsapSplitText();

	// Lancement de l'animation de chargement
	const loaderTimeline = gsap.timeline();
	loaderTimeline
		.to("#page-preloader .preloader__wrapper", {
			delay: 0.3,
			duration: 0.6,
			ease: "power1.out",
			opacity: 0,
		})
		.set("#page-preloader", {
			visibility: "hidden",
		});
	// Functions succédentes
	if (document.body.classList.contains("template-home")) {
		loaderTimeline.add(afterPreloader, loaderTimeline.duration());
	} else {
		loaderTimeline.add(afterPreloader, loaderTimeline.duration() - 0.3);
	}
};

/* --------------------------------- (ERWAN) -------------------------------- */

/**
 * Constantes Globales
 */
const { body } = document;
const urlParams = new URLSearchParams(window.location.search);

// Fonctions Utilitaires

/**
 * Nettoie et formate une chaîne de prix en nombre flottant.
 * @param {string} priceString - La chaîne de prix (ex: "1 250,50€").
 * @returns {number} Le prix formaté en flottant.
 */
const formatPriceToFloat = (priceString) => {
	if (!priceString) return 0;
	// Remplace l'euro, les espaces, et la virgule par un point pour le parseFloat
	const cleanedPrice = priceString
		.replace("€", "")
		.replace(/\s/g, "")
		.replace(",", ".");
	return parseFloat(cleanedPrice) || 0;
};

/**
 * Crée l'URL de redirection avec les paramètres de filtre mis à jour.
 * @param {string} name - Le nom du paramètre (ex: "brand").
 * @param {string} value - La valeur du paramètre (ex: ID ou valeur).
 * @returns {string} L'URL complète de redirection.
 */
const buildFilterUrl = (name, value) => {
	const newParams = new URLSearchParams(urlParams);

	// Supprime le paramètre existant s'il y en a un
	newParams.delete(name);

	// Ajoute le nouveau paramètre.
	// Pour le filtre radio, la nouvelle valeur est toujours ajoutée (sauf si on désélectionne, mais les radios ne le permettent pas naturellement)
	// Pour le checkbox, la valeur n'est ajoutée que si elle est cochée.
	if (value) {
		newParams.set(name, value);
	}

	// Optionnel : Réinitialise la pagination à 1 si un nouveau filtre est appliqué
	if (name !== "pages") {
		newParams.set("pages", "1");
	}

	return `${window.location.pathname}?${newParams.toString()}#produits`;
};

/**
 * Gère l'affichage du crédit pour le prix donné.
 * @param {number} priceTotal - Le prix total du produit.
 */
const updateCreditDisplay = (priceTotal) => {
	const legalsProduct = document.querySelector("#legals-product");
	const creditSimulation = document.querySelector("#credit-simulation");
	const minCreditPrice = 150; // Seuil minimum pour l'affichage du crédit (à adapter si nécessaire)

	if (priceTotal > minCreditPrice) {
		legalsProduct?.classList.remove("--hidden");

		// Affichage/Mise à jour du lien de simulation de crédit
		if (creditSimulation) {
			creditSimulation.href = `https://www.creditpartner.fr/simulateur/?Q6=web_em&X1=simu_vac&s3=${priceTotal}&a9=99102660690&n2=014`;
		}

		// Appel AJAX pour les données de Sofinco (Note: Dépend de jQuery)
		jQuery.ajax({
			method: "GET",
			url: `?lwb_sofinco=${priceTotal}`,
			beforeSend: () => {
				jQuery(".content__credit").addClass("--loading");
			},
			success: (values) => {
				jQuery(".content__credit").removeClass("--loading");

				const data = JSON.parse(values);
				// Assurer que la structure de données est correcte pour les deux appels (initial et change)
				const proposal = data?.proposals ? data.proposals[0] : data[0];

				if (proposal) {
					const {
						installmentWithoutInsurance,
						annualGlobalEffectiveRate,
						totalAmountWithoutInsurance,
					} = proposal;

					const formatCurrency = (amount) =>
						new Intl.NumberFormat("fr-FR", {
							style: "currency",
							currency: "EUR",
						}).format(amount);

					jQuery("#credit-price").html(
						formatCurrency(
							installmentWithoutInsurance
								? installmentWithoutInsurance.amount
								: proposal.monthlyAmountWithoutInsurance,
						),
					);

					jQuery("#credit-taeg").html(
						`TAEG ${
							annualGlobalEffectiveRate || proposal.annualPercentageRateOfCharge
						}%. Montant total dû : ${formatCurrency(
							totalAmountWithoutInsurance,
						)}`,
					);
				}
			},
			error: () => {
				console.error("Erreur lors de la récupération des données de crédit.");
			},
		});
	} else {
		legalsProduct?.classList.add("--hidden");
	}
};

/* -------- LOGIQUE DU FILTRE DE PRODUITS (sur .tax-product-category) ------- */

if (body && body.classList.contains("tax-product-category")) {
	/**
	 * Pré-sélection des filtres basés sur l'URL
	 */
	const filterKeys = ["brand", "material", "confort", "size", "price"];
	const filterElementsToSelect = filterKeys
		.map((key) => urlParams.get(key))
		.filter(Boolean) // Garde uniquement les valeurs non nulles/vides
		.map((value) => document.getElementById(value))
		.filter(Boolean); // Garde uniquement les éléments trouvés

	filterElementsToSelect.forEach((element) => {
		element.checked = true;
	});

	/**
	 * Gestion des événements de clic pour les filtres (Radio et Checkbox)
	 */

	// Gère les événements pour les Radio buttons (Ajoute/remplace le filtre)
	const radioFilters = document.querySelectorAll("input[type='radio']");
	radioFilters.forEach((element) => {
		element.addEventListener("click", (event) => {
			const { name, id } = event.target;
			window.location.href = buildFilterUrl(name, id);
		});
	});

	// Gère les événements pour les Checkbox (Ajoute/supprime le filtre - NOTE : Le code original est étrange pour les checkbox, car il supprime l'existant puis redirige en ajoutant le nouveau, comme un radio. J'ai gardé la logique de l'original pour le moment, mais une vraie logique de checkbox nécessiterait de gérer plusieurs valeurs)
	const checkboxFilters = document.querySelectorAll("input[type='checkbox']");
	checkboxFilters.forEach((element) => {
		element.addEventListener("click", (event) => {
			const { name, value, checked } = event.target;
			// Le code original semble traiter les checkboxes comme des radios (un seul choix possible pour le paramètre), mais j'utilise la "value" au lieu de l'ID.
			window.location.href = buildFilterUrl(name, checked ? value : ""); // Utilise value si coché, sinon vide pour supprimer
		});
	});

	/**
	 * Bouton "Nettoyer les filtres"
	 */
	const cleanFilter = document.querySelector("#clean-filter");
	cleanFilter?.addEventListener("click", () => {
		window.location.href = window.location.pathname + "#produits"; // Redirige vers la page sans paramètres
	});
}

/* ------------------ LOGIQUE DE LA PAGE DE DÉTAIL PRODUIT ------------------ */

const selectPrice = document.querySelector("#select-size");
const initialSize = urlParams.get("size");

if (selectPrice) {
	/**
	 * Initialisation de la taille et du prix
	 */

	const updatePriceDisplay = (selectedIndex) => {
		const prices = document.querySelectorAll("#prices > *");
		const currentPrice = document.querySelector(`#price-${selectedIndex}`);

		// Cache tous les blocs de prix
		prices.forEach((el) => el.classList.add("--hidden"));

		// Affiche le bloc de prix courant
		if (currentPrice) {
			currentPrice.classList.remove("--hidden");

			const priceTotalElt = currentPrice.querySelector('[data-price="total"]');
			const priceEcoElt = currentPrice.querySelector('[data-price="eco"]');

			const priceTotalText = priceTotalElt ? priceTotalElt.innerHTML : "";
			const priceEcoText = priceEcoElt ? priceEcoElt.innerHTML : "";

			const priceTotalValue = formatPriceToFloat(priceTotalText);
			const priceEcoValue = formatPriceToFloat(priceEcoText);

			// [data-copy-price="total"]
			const copyPriceTotal = document.querySelectorAll(
				'[data-copy-price="total"]',
			);
			copyPriceTotal.forEach((target) => {
				target.innerHTML = priceTotalText;
			});

			// [data-copy-price="eco"]
			const copyPriceEco = document.querySelectorAll('[data-copy-price="eco"]');
			copyPriceEco.forEach((target) => {
				target.innerHTML = priceEcoValue.toFixed(2).replace(".", ",");
			});

			// Met à jour l'affichage du crédit
			updateCreditDisplay(priceTotalValue);
		}
	};

	if (initialSize) {
		// Si un paramètre 'size' est dans l'URL, sélectionne l'option et met à jour le prix
		Array.from(selectPrice.options).forEach((option_element, index) => {
			if (initialSize === option_element.text) {
				selectPrice.value = option_element.value;
				updatePriceDisplay(index);
			}
		});
	} else {
		// Si pas de paramètre, utilise l'état initial (souvent la première option)
		updatePriceDisplay(0);
	}

	/**
	 * Événement 'change' sur le sélecteur de taille
	 */
	selectPrice.addEventListener("change", (event) => {
		const selectedIndex = event.target.selectedIndex;
		const selectedText = event.target.options[selectedIndex].text;
		updatePriceDisplay(selectedIndex);
	});
}

/* ------------------- LOGIQUE DES OFFRES D'EMPLOI (JOBS) ------------------- */

const jobParam = urlParams.get("offre");
const selectJob = document.querySelector("#jobs");

if (jobParam && selectJob) {
	const job = document.querySelector(`[data-id='${jobParam}']`);
	if (job) {
		selectJob.value = job.value;
	}
}

/* -------- LOGIQUE DE LA CARTE DE RECHERCHE DE MAGASINS (SEARCHMAP) -------- */

/**
 * Ajoute/Met à jour les magasins et la carte pour la ville donnée.
 * @param {string} city - La ville à rechercher.
 */
const addShop = (city) => {
	const pageSearchMap = document.querySelector("#page-search-map");
	const listShop = document.querySelector("#list-shop");
	const mapDiv = document.querySelector(".searchmap__mapblock");
	const existingMap = document.querySelector("#map-city-choice");

	if (!pageSearchMap || !listShop || !mapDiv) return;
	const url = window.location.origin;

	// Appel AJAX (Note: Dépend de jQuery)
	jQuery.ajax({
		url: `${url}/shops-json/`,
		data: {
			ajax: 1,
			ville: city,
		},
		beforeSend: () => {
			pageSearchMap.classList.add("--loading");
			jQuery(".loader-map").show();
		},
		success: (response) => {
			pageSearchMap.classList.remove("--loading");
			pageSearchMap.classList.add("--results"); // Garde la popin élargie avec la carte
			jQuery(".loader-map").hide();

			listShop.innerHTML = ""; // Vide la liste

			if (existingMap) {
				existingMap.remove(); // Supprime la carte existante pour la recréer
			}

			const newMap = document.createElement("div");
			newMap.className = "searchmap__map";
			newMap.id = "map-city-choice";
			mapDiv.appendChild(newMap);

			// Initialisation de la carte Leaflet
			const defaultCoords = [48.8566, 2.3522]; // Paris
			const coordinates = response.city
				? [response.city.lat, response.city.lon]
				: defaultCoords;

			// Utilisation d'un objet de configuration pour la carte
			const mapConfig = {
				center: coordinates,
				zoom: response.city ? 10 : 7, // Zoom plus proche si une ville spécifique est trouvée
				scrollWheelZoom: true,
			};

			const leafletMap = L.map(newMap, mapConfig);
			L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png").addTo(
				leafletMap,
			);

			const markerHtml = `
            <i style="transform: translate(-40%, -60%); filter: drop-shadow(13px 15px 10px rgba(0, 0, 0, 0.33))">
              <svg xmlns="http://www.w3.org/2000/svg" height="50" viewBox="0 0 39.7 48.88">
                <path fill="var(--color-secondary)" d="M19.85 0C8.89 0 0 8.89 0 19.85c0 7.44 6.7 17.12 19.85 29.03C33 37.22 39.7 27.29 39.7 19.85 39.7 8.89 30.81 0 19.85 0Zm.34 32.88c-7.73 0-14-6.27-14-14s6.27-14 14-14 14 6.27 14 14-6.27 14-14 14Z"/>
                <path fill="var(--color-light)" d="M20.19 4.88c-7.73 0-14 6.27-14 14s6.27 14 14 14 14-6.27 14-14-6.27-14-14-14Zm0 26c-6.29 0-11.45-4.84-11.96-11h23.92c-.51 6.16-5.67 11-11.96 11Z"/>
                <path fill="var(--color-primary)" d="M32.15 19.88c-.51 6.16-5.67 11-11.96 11s-11.45-4.84-11.96-11h23.92Z"/>
              </svg>
            </i>
          `;

			if (response.shops && response.shops.length > 0) {
				response.shops.forEach((shop) => {
					const { title, localisation, phone, id, latitude, longitude } = shop;

					// Ajout à la liste
					listShop.innerHTML += `
              <div class="excerpt-shop">
                <div class="excerpt__wrapper">
                  <div class="excerpt__header">
                    <div class="excerpt__hgroup">
                      <p class="excerpt__title title-h5">${title}</p>
                      <p class="excerpt__subtitle title-h0">
                        ${localisation.adresse}, ${localisation.code_postal}, ${localisation.ville}
                      </p>
                    </div>
                    <div class="excerpt__actions">
                      <button class="exerpt__button button-primary" data-shop="${id}">
                        <span>Choisir ce magasin</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            `;

					// Ajout du marqueur sur la carte
					const marker = L.marker([latitude, longitude], {
						icon: L.divIcon({
							html: markerHtml,
						}),
					});
					leafletMap.addLayer(marker);

					// Création de la popup
					const popup = `
              <div class="pin">
                <div class="pin__title">${title}</div>
                <div class="pin__address">
                  ${localisation.adresse}<br>
                  ${localisation.code_postal} ${localisation.ville}
                </div>
                <div class="pin__phone">${phone}</div>
              </div>
            `;
					marker.bindPopup(popup);
				});
			} else {
				listShop.innerHTML = "<blockquote>Aucun résultat...</blockquote>";
			}

			// Mise à jour des gestionnaires d'événements pour les boutons "Choisir ce magasin" (à l'intérieur de la fonction car les éléments sont recréés)
			document.querySelectorAll("[data-shop]").forEach((element) => {
				element.addEventListener("click", () => {
					const uri = document.body.dataset.uri;
					// Appel AJAX (Note: Dépend de jQuery)
					jQuery.ajax({
						url: `${uri}/inc/session_shop.php`,
						data: {
							shop: element.dataset.shop,
						},
						success: () => {
							pageSearchMap.classList.remove("--open");
							location.reload(); // Rechargement après la sélection
						},
						error: () => {
							console.error("Erreur lors de la sélection du magasin.");
						},
					});
				});
			});
		},
		error: () => {
			pageSearchMap.classList.remove("--loading");
			jQuery(".loader-map").hide();
			listShop.innerHTML =
				"<blockquote>Une erreur est survenue lors de la recherche.</blockquote>";
		},
	});
};

const searchCity = document.querySelector("#search-city");
const searchCitySubmit = document.querySelector("#search-city-submit");

if (searchCity) {
	// Événement Enter pour la recherche
	searchCity.addEventListener("keyup", (el) => {
		if (el.key === "Enter") {
			addShop(el.target.value);
		}
	});
}

if (searchCitySubmit) {
	// Événement Clic pour la recherche
	searchCitySubmit.addEventListener("click", () => {
		if (searchCity) {
			addShop(searchCity.value);
		}
	});
}

/* ------------------ LOGIQUE DU POPUP CTA (CALL TO ACTION) ----------------- */

const ctaOffer = document.querySelector(".page__cta-offer");

if (ctaOffer) {
	const ctaOfferKey = "cta_offer";

	// Ouvre le CTA s'il n'a jamais été vu dans la session
	if (sessionStorage.getItem(ctaOfferKey) !== "true") {
		ctaOffer.classList.add("--open");
		sessionStorage.setItem(ctaOfferKey, "true");
	} else {
		// Si déjà vu, assure qu'il est fermé (par défaut, il devrait être fermé en CSS)
		ctaOffer.classList.remove("--open");
	}

	// Ferme le CTA si on clique à l'extérieur
	document.addEventListener("mouseup", (event) => {
		if (
			ctaOffer.classList.contains("--open") &&
			!ctaOffer.contains(event.target) &&
			ctaOffer !== event.target
		) {
			ctaOffer.classList.remove("--open");
		}
	});
}
